import {
  Avatar,
  Box,
  FlexBox,
  Text,
  Timestamp,
  Tooltip,
} from '@dixa/lib-shared-habla';
import cx from 'classnames';
import { SourceConversationsChip } from 'lib-knowledge-utils/components/sourceConversations/index';
import { forwardRef } from 'react';

import { Suggestion } from '../utils/toSuggestion';

import s from './suggestionRow.module.scss';
import { SuggestionTypeBadge } from './suggestionTypeBadge';

type SuggestionRowProps = {
  suggestion: Suggestion;
  isSelected: boolean;
  onSelect: (suggestion: Suggestion) => void;
};

/**
 * One suggestion in its group's list. Renders the `li` itself, and forwards the
 * ref the group attaches to the last row to trigger paging.
 *
 * The title is a button, and it is the only thing here that takes focus: it
 * carries the accessible name and gives Enter and Space for free. The click it
 * raises bubbles to the row, which is what selects — so a pointer can land
 * anywhere on the row, and no key handling of our own is needed.
 *
 * Not a `listbox` of `option`s: that pattern wants one tab stop for the whole
 * list with arrow keys between the options, and forbids interactive content
 * inside an option — but every row carries a conversations chip and a
 * timestamp of its own.
 */
export const SuggestionRow = forwardRef<HTMLLIElement, SuggestionRowProps>(
  function SuggestionRow({ suggestion, isSelected, onSelect }, ref) {
    const { type, title, summary, conversations, initiatedBy } = suggestion;

    const breadcrumb = [suggestion.collectionName, suggestion.categoryName]
      .filter(Boolean)
      .join(' · ');

    return (
      <li
        ref={ref}
        className={cx(s.row, { [s.rowSelected]: isSelected })}
        onClick={() => onSelect(suggestion)}
      >
        <FlexBox flexDirection="column" gap="200">
          <Box margin="0 0 100">
            <SuggestionTypeBadge type={type} />
          </Box>

          <button
            type="button"
            className={s.titleButton}
            aria-current={isSelected ? 'true' : undefined}
          >
            <Text.Paragraph as="span" variant="2">
              <strong>{title}</strong>
            </Text.Paragraph>
          </button>

          <Text.Paragraph variant="4" subdued clamp={2}>
            {summary}
          </Text.Paragraph>

          {breadcrumb && (
            <Text.Paragraph variant="3" subdued>
              {breadcrumb}
            </Text.Paragraph>
          )}

          <FlexBox
            alignItems="center"
            justifyContent="space-between"
            gap="200"
            margin="100 0 0"
          >
            <FlexBox alignItems="center" gap="100">
              <SourceConversationsChip conversations={conversations} />
              {initiatedBy && (
                <Tooltip content={`Initiated by: ${initiatedBy.name}`}>
                  <Avatar
                    size="24"
                    user={{
                      name: initiatedBy.name,
                      email: initiatedBy.email,
                      avatar_url: initiatedBy.avatarUrl,
                    }}
                  />
                </Tooltip>
              )}
            </FlexBox>

            <Text.Paragraph variant="4" subdued>
              <Timestamp
                time={suggestion.activityAt / 1000}
                relative
                refreshRate={60000}
              />
            </Text.Paragraph>
          </FlexBox>
        </FlexBox>
      </li>
    );
  },
);
